import { useState, useEffect, useCallback, useRef } from 'react';
import { SideBySideData, CardData } from '../../../types';
import { buildApiPayload, defaultSideBySideData, parseApiResponse } from '../../../lib/customization/sideBySideMapper';
import { validateSideBySideData } from '../../../lib/customization/sideBySideValidator';
import { getCustomizationSection, saveCustomizationSection, uploadCustomizationImage } from '../../../lib/customization/customizationService';

export const useSideBySide = (storeId: string) => {
    const [data, setData] = useState<SideBySideData>(defaultSideBySideData);
    const [originalData, setOriginalData] = useState<SideBySideData | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success',
    });
    const [saveError, setSaveError] = useState<string | null>(null);

    const [uploading1, setUploading1] = useState(false);
    const [uploading2, setUploading2] = useState(false);
    const uploadingRef = useRef(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const parsedData = parseApiResponse(await getCustomizationSection<any>(storeId, 'SideBySide'));
            setData(parsedData);
            setOriginalData(parsedData);
        } catch (error) {
            console.error('Error fetching side by side data:', error);
            setNotification({ message: 'Failed to load data. Please try again later.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const checkForChanges = useCallback((newData: SideBySideData): boolean => {
        if (!originalData) return false;
        return JSON.stringify(newData) !== JSON.stringify(originalData);
    }, [originalData]);

    const updateData = useCallback((updater: (prev: SideBySideData) => SideBySideData) => {
        setData((prev) => {
            const newData = updater(prev);
            setHasChanges(checkForChanges(newData));
            return newData;
        });
    }, [checkForChanges]);

    const updateSection = useCallback((field: keyof SideBySideData, value: any) => {
        updateData((prev) => ({ ...prev, [field]: value }));
    }, [updateData]);

    const updateCard = useCallback((cardNum: 1 | 2, field: keyof CardData, value: any) => {
        updateData((prev) => ({
            ...prev,
            [`card${cardNum}`]: {
                ...prev[`card${cardNum}`],
                [field]: value,
            },
        }));
    }, [updateData]);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, cardNum: 1 | 2) => {
        if (uploadingRef.current) return;
        
        const inputTarget = e.target; 
        const file = inputTarget.files?.[0];
        if (!file) return;

        const MAX_FILE_SIZE = 1024 * 1024; // 1MB

        if (file.size > MAX_FILE_SIZE) {
            setNotification({
                message: `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024} MB.`,
                type: "error",
            });
            return;
        }

        uploadingRef.current = true;
        if (cardNum === 1) setUploading1(true);
        else setUploading2(true);

        const clean = file.name.replace(/[^\w.-]/g, "_");
        const sanitized = new File([file], clean, { type: file.type });

        try {
            const url = await uploadCustomizationImage(storeId || '', sanitized);

            updateCard(cardNum, 'image', url);

        } catch (error: any) {
            console.error('Error uploading image:', error);
            setNotification({ 
                message: "Image upload failed. Please try again.", 
                type: 'error' 
            });
        } finally {
            uploadingRef.current = false;
            if (cardNum === 1) setUploading1(false);
            else setUploading2(false);
            
            inputTarget.value = ''; 
        }
    };

    const swapCards = useCallback(() => {
        updateData((prev) => ({
            ...prev,
            card1: prev.card2,
            card2: prev.card1,
        }));
    }, [updateData]);

    const saveData = useCallback(async (): Promise<boolean> => {
        const validationError = validateSideBySideData(data);
        if (validationError) {
            setSaveError(validationError);
            return false;
        }

        setLoading(true);
        setSaveError(null);

        try {
            await saveCustomizationSection({
                storeId,
                metafield: 'SideBySide',
                section: 'sideBySide',
                payload: buildApiPayload(data),
                lastValue: originalData,
                newValue: data,
            });

            setNotification({ message: 'Side by Side cards saved successfully!', type: 'success' });
            setOriginalData(data);
            setHasChanges(false);
            return true;
        } catch (error) {
            console.error('Error saving side by side data:', error);
            setSaveError('Failed to save. Please try again.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [data, originalData, storeId]);

    const cancelChanges = useCallback(() => {
        if (originalData) {
            setData(originalData);
            setHasChanges(false);
        }
        setSaveError(null);
    }, [originalData]);

    const clearNotification = useCallback(() => {
        setNotification({ message: null, type: 'success' });
    }, []);

    const clearSaveError = useCallback(() => {
        setSaveError(null);
    }, []);

    return {
        data,
        loading,
        hasChanges,
        notification,
        saveError,
        updateSection,
        updateCard,
        swapCards,
        saveData,
        cancelChanges,
        clearNotification,
        clearSaveError,
        uploading1,
        uploading2,
        handleImageUpload,
    };
};